import { useCallback } from 'react';
import { safesService } from '../services';
import {
  useResourceCollection,
  useResourceById,
  useCreateResource,
  useUpdateResource,
  useDeleteResource,
  ResourceState,
  SingleResourceState,
  MutationState,
} from './useResource';
import { ResourceQueryOptions } from '../services/resources.service';
import { MoneySafe } from '../types';

export function useSafes(
  options?: ResourceQueryOptions
): ResourceState<MoneySafe> & { meta?: any } {
  const fetchFn = useCallback(
    (opts?: ResourceQueryOptions) =>
      safesService.getAll(opts) as unknown as Promise<{ data: MoneySafe[]; meta?: any }>,
    []
  );
  return useResourceCollection<MoneySafe>(fetchFn, options);
}

export function useMoneySafe(
  id: string | null,
  options?: ResourceQueryOptions
): SingleResourceState<MoneySafe> {
  const numericId = id ? parseInt(id, 10) : null;
  const fetchFn = useCallback(
    (moneyId: number, opts?: ResourceQueryOptions) =>
      safesService.getById(moneyId, opts) as unknown as Promise<MoneySafe>,
    []
  );
  return useResourceById<MoneySafe>(fetchFn, numericId, options);
}

export function useCreateMoneySafe(
  onSuccess?: (data: MoneySafe) => void
): MutationState<MoneySafe> {
  const createFn = useCallback(
    (data: any) => safesService.create(data) as unknown as Promise<MoneySafe>,
    []
  );
  return useCreateResource<MoneySafe>(createFn, onSuccess);
}

export function useUpdateMoneySafe(
  onSuccess?: (data: MoneySafe) => void
): MutationState<MoneySafe> {
  const updateFn = useCallback(
    (id: string | number, data: any) => {
      const numericId = typeof id === 'string' ? parseInt(id, 10) : id;
      return safesService.update(numericId, data) as unknown as Promise<MoneySafe>;
    },
    []
  );
  return useUpdateResource<MoneySafe>(updateFn, onSuccess);
}

/**
 * Delete money using documentId
 * @param data - Full money object containing documentId
 */
export function useDeleteMoneySafe(onSuccess?: () => void): MutationState<void> {
  const deleteFn = useCallback(
    (data: any) => {
      const documentId = data.documentId || data.id?.toString() || data.id;
      return safesService.delete(documentId) as unknown as Promise<void>;
    },
    []
  );
  return useDeleteResource(deleteFn, onSuccess);
}
// Auto-generated hook file
